import {
	isRootNode,
	isSharedWithMeNode,
} from "@/ui/components/FileBrowser/types";
import { isFileOwner } from "@/ui/components/FileBrowser/actionAvailability";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

/**
 * What a drag carries: the selection it started from, which can hold files and
 * folders at once. Either list may be empty; both empty is nothing to drop.
 */
export type DragPayload = {
	files: FileDTO[];
	folders: ResourceTreeNodeDO[];
};

/**
 * Depth cap on the ancestor walk. The backend does not reject a cycle, so if
 * one ever reaches the tree the walk has to terminate anyway.
 */
const MAX_ANCESTOR_DEPTH = 100;

/** Whether `candidate` sits anywhere beneath `ancestorId`. */
const isDescendantOf = (
	candidate: ResourceTreeNodeDO,
	ancestorId: number,
	folders: ResourceTreeNodeDO[]
): boolean => {
	let current: ResourceTreeNodeDO | undefined = candidate;

	for (let depth = 0; depth < MAX_ANCESTOR_DEPTH; depth++) {
		if (current?.parent_id == null) {
			return false;
		}

		if (current.parent_id === ancestorId) {
			return true;
		}

		current = folders.find((folder) => folder.id === current?.parent_id);
	}

	return false;
};

/**
 * A file lives in its owner's tree, so only the owner may move it. Anything in
 * "Shared with me" therefore stays put: dragging it would either silently fail
 * on the update, or move it out from under the person it belongs to.
 */
const isMovableFile = (file: FileDTO): boolean => isFileOwner(file);

/** A folder the user may reparent at all, before any target is considered. */
const isMovableFolder = (folder: ResourceTreeNodeDO): boolean =>
	!isRootNode(folder) && !isSharedWithMeNode(folder);

/**
 * Whether a drag may start at all.
 *
 * Refuses a mixed selection outright rather than quietly moving the movable
 * half: the same call decides whether the row is `draggable`, so a refusal
 * shows up as a row that will not lift rather than as a surprise afterwards.
 */
export const canStartDrag = (payload: DragPayload): boolean => {
	if (!payload.files.length && !payload.folders.length) {
		return false;
	}

	return (
		payload.files.every(isMovableFile) && payload.folders.every(isMovableFolder)
	);
};

/**
 * A folder move that must never fire, as opposed to one that merely changes
 * nothing. Dropping a multi-folder selection onto one of its own members counts
 * as forbidden rather than a no-op: the gesture is ambiguous, and the folder
 * endpoint takes any `parent_id` it is given, so an orphaning move would go
 * through with no error.
 */
const isForbiddenFolderMove = (
	folder: ResourceTreeNodeDO,
	target: ResourceTreeNodeDO,
	folders: ResourceTreeNodeDO[]
): boolean =>
	// Root and the shared bucket are not the user's to move.
	!isMovableFolder(folder) ||
	// Onto itself.
	folder.id === target.id ||
	// Into its own subtree, which would orphan the branch.
	isDescendantOf(target, folder.id, folders);

/**
 * Whether a drag may be dropped on a folder: nothing it carries may be
 * forbidden, and at least one thing has to actually move.
 *
 * The drop target refuses invalid moves rather than letting them fire - see
 * isForbiddenFolderMove.
 */
export const canDropOnFolder = (
	payload: DragPayload | null,
	target: ResourceTreeNodeDO | null | undefined,
	folders: ResourceTreeNodeDO[]
): boolean => {
	if (!payload || !target || !canStartDrag(payload)) {
		return false;
	}

	// "Shared with me" is a view over other people's files, not a folder.
	if (isSharedWithMeNode(target)) {
		return false;
	}

	if (
		payload.folders.some((folder) =>
			isForbiddenFolderMove(folder, target, folders)
		)
	) {
		return false;
	}

	const movesAFile = payload.files.some(
		(file) => file.resource_tree_node_id !== target.id
	);
	const movesAFolder = payload.folders.some(
		(folder) => folder.parent_id !== target.id
	);

	return movesAFile || movesAFolder;
};
